import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Heart, Plus, Sparkles } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';

/**
 * ThankYouWall — public widget on /support that shows the last 3
 * approved donor names and lets a recent donor opt in to be added.
 * Entries are admin-moderated before they surface publicly.
 */
const ThankYouWall = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState({ display_name: '', amount_usd: '', message: '' });

  const load = async () => {
    const { data } = await apiFetch('/api/support/thank-you-wall');
    if (data?.entries) setEntries(data.entries);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onSubmit = async () => {
    const name = draft.display_name.trim();
    if (!name) {
      toast.error(isSpanish ? 'Tu nombre es obligatorio' : 'Display name is required');
      return;
    }
    setSubmitting(true);
    const body = { display_name: name };
    if (draft.amount_usd) body.amount_usd = Number(draft.amount_usd);
    if (draft.message.trim()) body.message = draft.message.trim();
    const { data, error } = await apiFetch('/api/support/thank-you-submit', {
      method: 'POST',
      body,
    });
    setSubmitting(false);
    if (error || !data?.ok) {
      toast.error(error?.detail || (isSpanish ? 'Error al enviar' : 'Submission failed'));
      return;
    }
    toast.success(
      isSpanish
        ? '¡Gracias! Tu nombre aparecerá tras una breve revisión.'
        : 'Thank you! Your name will appear after a quick review.'
    );
    setShowForm(false);
    setDraft({ display_name: '', amount_usd: '', message: '' });
  };

  return (
    <Card
      className="mt-6 border-pink-300 dark:border-pink-700/50 bg-gradient-to-br from-pink-50/60 to-amber-50/40 dark:from-pink-950/20 dark:to-amber-950/10"
      data-testid="thank-you-wall"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Heart className="h-4 w-4 text-pink-500 fill-pink-500" />
          {isSpanish ? 'Muro de agradecimiento' : 'Thank-you wall'}
          <span className="ml-auto text-[11px] font-normal text-muted-foreground">
            {isSpanish ? 'Últimos 3 partidarios' : 'Latest 3 supporters'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="thank-you-empty">
            {isSpanish
              ? '¡Sé la primera persona en aparecer aquí!'
              : 'Be the first person to appear here!'}
          </p>
        ) : (
          <div className="space-y-2" data-testid="thank-you-entries">
            {entries.map((e, i) => (
              <div
                key={`${e.display_name}-${i}`}
                className="rounded-lg bg-white/70 dark:bg-black/30 backdrop-blur px-3 py-2 flex items-start gap-2"
                data-testid={`thank-you-entry-${i}`}
              >
                <Sparkles className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-baseline gap-2">
                    <span>{e.display_name}</span>
                    {e.amount_usd != null && (
                      <span className="text-[11px] font-mono text-muted-foreground">
                        ${Number(e.amount_usd).toFixed(0)}
                      </span>
                    )}
                  </div>
                  {e.message && (
                    <p className="text-xs text-muted-foreground italic mt-0.5">
                      “{e.message}”
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!showForm ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowForm(true)}
            data-testid="thank-you-open-form"
            className="w-full border-pink-300 hover:bg-pink-50 dark:hover:bg-pink-950/30"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            {isSpanish ? 'Acabo de donar — añádeme' : 'I just donated — add me'}
          </Button>
        ) : (
          <div className="space-y-2 border-t pt-3" data-testid="thank-you-form">
            <Input
              value={draft.display_name}
              onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
              placeholder={isSpanish ? 'Tu nombre (o apodo)' : 'Your name (or nickname)'}
              maxLength={60}
              data-testid="thank-you-name-input"
            />
            <Input
              type="number"
              min={0}
              step={1}
              value={draft.amount_usd}
              onChange={(e) => setDraft({ ...draft, amount_usd: e.target.value })}
              placeholder={isSpanish ? 'Monto USD (opcional)' : 'Amount USD (optional)'}
              data-testid="thank-you-amount-input"
            />
            <Textarea
              value={draft.message}
              onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              placeholder={isSpanish ? 'Mensaje corto (opcional)' : 'Short message (optional)'}
              maxLength={240}
              rows={2}
              data-testid="thank-you-message-input"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={onSubmit}
                disabled={submitting}
                className="bg-pink-500 hover:bg-pink-600 text-white flex-1"
                data-testid="thank-you-submit-btn"
              >
                <Heart className="h-3.5 w-3.5 mr-1" />
                {submitting
                  ? (isSpanish ? 'Enviando…' : 'Submitting…')
                  : (isSpanish ? 'Añádeme' : 'Add me')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowForm(false)}
                data-testid="thank-you-cancel-btn"
              >
                {isSpanish ? 'Cancelar' : 'Cancel'}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              {isSpanish
                ? 'Las entradas son moderadas — gracias por tu paciencia.'
                : 'Entries are admin-moderated — thanks for your patience.'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ThankYouWall;
